<div class="flex w-full h-full">
  <!-- Left panel - Technician Selection -->
  <app-technician-selection-panel
    [rangeDates]="rangeDates"
    [users]="users"
    [loadingUsers]="loadingUsers"
    [selectedTechnicianControl]="selectedTechnicianControl"
    [closestTechniciansByRoute]="closestTechniciansByRoute"
    [loadingTechniciansByRoute]="loadingTechniciansByRoute"
    (technicianSelected)="onTechnicianSelected($event)"
    (homeTechnicianClicked)="onHomeTechnicianDirectSelect($event)"
  ></app-technician-selection-panel>

  <p-divider layout="vertical" styleClass="mx-1"></p-divider>

  <!-- Map container -->
  <div class="flex-1 map-container">
    @if (!hasValidCoordinates()) {
      <div
        class="h-full flex items-center justify-center bg-neutral-900 rounded-lg"
      >
        <div class="text-center p-4">
          <i class="pi pi-map-marker text-4xl text-orange-400 mb-3"></i>
          <p-message severity="warn" styleClass="w-full mb-3">
            <ng-template pTemplate="content">
              <div class="flex flex-col items-start">
                <span class="font-medium">Impossible d'afficher la carte</span>
                <span class="text-sm mt-1"
                  >Les coordonnées GPS du chantier sont manquantes ou
                  invalides.</span
                >
              </div>
            </ng-template>
          </p-message>
          <p class="text-sm text-neutral-400 mt-2">
            Veuillez vérifier et mettre à jour l'adresse du chantier dans Éros.
          </p>
        </div>
      </div>
    } @else {
      <app-map-with-markers
        [markers]="allMarkers()"
        [route]="dailyRoute()"
        [projectedRoute]="projectedRoute()"
        [distanceLines]="distanceLines()"
        [departmentBoundaries]="departmentBoundaries()"
        [departmentGeoJson]="departmentGeoJson"
        styleClass="map-full-size"
        mapStyle="minimal"
      ></app-map-with-markers>

      <!-- Daily info panel (shared component) -->
      @if (dailyCalendarEvents.length > 0 || projectedRouteInfo) {
        <app-daily-info-panel
          [selectedDate]="selectedIntervention?.startDate"
          [dailyCalendarEvents]="dailyCalendarEvents"
          [dailyRouteInfo]="dailyRouteInfo"
          [projectedRouteInfo]="projectedRouteInfo"
          [loadingDailyRoute]="loadingDailyRoute"
          [preSelectedSlot]="preSelectedSlot"
          [newItemName]="getNewItemName()"
          [newItemCityWithPostalCode]="getAffairCityWithPostalCode()"
          [labelNewItem]="labelNewItem"
          [technicianColor]="getSelectedTechnicianColor()"
          [technicianHomeCity]="getSelectedTechnicianHomeCity()"
          [hasHomeCoordinates]="hasSelectedTechnicianHomeCoordinates()"
          [startLocationType]="startLocationType"
          [endLocationType]="endLocationType"
          [hasStartLocationCoordinates]="
            startLocationCoords !== null ||
            hasSelectedTechnicianHomeCoordinates()
          "
          [hasEndLocationCoordinates]="
            endLocationCoords !== null || hasSelectedTechnicianHomeCoordinates()
          "
          [startLocationCity]="startLocationCity"
          [endLocationCity]="endLocationCity"
          [startTravelUncounted]="startTravelUncounted"
          [endTravelUncounted]="endTravelUncounted"
          [currentRouteDeparture]="currentRouteDeparture"
          [currentRouteReturn]="currentRouteReturn"
          [projectedRouteReturn]="projectedRouteReturn"
          [googleMapsUrlCurrentRoute]="getGoogleMapsUrlCurrentRoute()"
          [googleMapsUrlWithNewItem]="getGoogleMapsUrlWithAffair()"
        ></app-daily-info-panel>
      } @else if (loadingDailyRoute) {
        <div class="route-info-overlay loading">
          <p-progressSpinner
            [style]="{ width: '20px', height: '20px' }"
            strokeWidth="4"
          ></p-progressSpinner>
          <span class="ml-2 text-sm">Calcul de l'itinéraire...</span>
        </div>
      }
    }
  </div>

  <p-divider layout="vertical" styleClass="mx-1"></p-divider>

  <!-- Right panel - Split into two sections -->
  <div class="interventions-panel">
    <!-- Pre-selected slot display -->
    @if (preSelectedSlot) {
      <div
        class="pre-selected-slot-banner bg-blue-900/50 border border-blue-500 p-2 mb-2 rounded-lg"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="pi pi-check-circle text-blue-400 mr-2"></i>
            <div>
              <div class="text-sm font-medium text-blue-300">
                Créneau pré-sélectionné
              </div>
              <div class="text-xs text-blue-200">
                {{ getPreSelectedSlotInfo() }}
              </div>
            </div>
          </div>
          <button
            type="button"
            class="text-blue-400 hover:text-blue-300 p-1"
            (click)="clearPreSelectedSlot()"
            title="Annuler la pré-sélection"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>
      </div>
    }

    <!-- Top Section: Interventions -->
    <div class="interventions-section">
      <app-closest-interventions-list
        [interventions]="closestInterventions"
        [selectedIntervention]="selectedIntervention"
        [loadingInterventions]="loadingInterventions"
        [loadingBatchIndicators]="loadingBatchIndicators"
        [loadingSlotTravelInfo]="loadingSlotTravelInfo"
        [slotTravelInfo]="slotTravelInfo"
        [interventionSlotIndicators]="interventionSlotIndicators"
        [sortBy]="interventionsSortBy"
        [limit]="interventionsLimit"
        (interventionSelected)="onSelectIntervention($event)"
        (interventionMouseEnter)="onInterventionMouseEnter($event)"
        (interventionMouseLeave)="onInterventionMouseLeave()"
        (refreshClicked)="refreshInterventionsAndAvailabilities()"
        (sortByChanged)="updateInterventionsSortBy($event)"
        (limitChanged)="updateInterventionsLimit($event)"
      ></app-closest-interventions-list>
    </div>

    <!-- Divider between sections -->
    <div class="border-t border-neutral-700 my-2"></div>

    <!-- Bottom Section: Availabilities -->
    <div class="availabilities-section">
      <app-availabilities-panel
        [selectedIntervention]="selectedIntervention"
        [selectedTechnicianDirect]="selectedTechnicianDirect"
        [selectedInterventionAvailabilities]="
          selectedInterventionAvailabilities
        "
        [loadingSelectedInterventionAvailabilities]="
          loadingSelectedInterventionAvailabilities
        "
        [loadingSlotTravelInfo]="loadingSlotTravelInfo"
        [slotTravelInfo]="slotTravelInfo"
        [interventionDuration]="interventionDuration"
        [technicianColors]="technicianColorsMap"
        [technicianInitials]="technicianInitialsMap"
        [emptyDaysSlots]="emptyDaysSlots"
        [loadingEmptyDays]="loadingEmptyDays"
        [initialTab]="preferredAvailabilityTab"
        (closePanel)="clearSelectedIntervention()"
        (slotSelected)="preSelectTimeSlot($event.slot, $event.userEmail)"
        (emptyDaySlotSelected)="
          preSelectEmptyDayTimeSlot($event.slot, $event.userEmail, $event.date)
        "
      />
    </div>
  </div>
</div>
